/**
 * Panel Shell Icons
 * Dependency-free inline SVGs matching the lucide icons the shell chrome
 * needs (close grips, pins, chevrons, alignment glyphs). Keeping them in the
 * package means no app needs lucide-react to compile the shell; app-declared
 * panel/tab icons stay app-supplied `ReactNode`s.
 */

import React from 'react';

export interface PanelIconProps {
    className?: string;
    size?: number | string;
    strokeWidth?: number;
}

const Svg: React.FC<PanelIconProps & { children: React.ReactNode; viewBox?: string }> = ({
    className,
    size = 24,
    strokeWidth = 2,
    children,
}) => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={className}
        aria-hidden="true"
    >
        {children}
    </svg>
);

export const X: React.FC<PanelIconProps> = (props) => (
    <Svg {...props}>
        <path d="M18 6 6 18" />
        <path d="m6 6 12 12" />
    </Svg>
);

export const Check: React.FC<PanelIconProps> = (props) => (
    <Svg {...props}>
        <path d="M20 6 9 17l-5-5" />
    </Svg>
);

export const GripVertical: React.FC<PanelIconProps> = (props) => (
    <Svg {...props}>
        <circle cx="9" cy="12" r="1" />
        <circle cx="9" cy="5" r="1" />
        <circle cx="9" cy="19" r="1" />
        <circle cx="15" cy="12" r="1" />
        <circle cx="15" cy="5" r="1" />
        <circle cx="15" cy="19" r="1" />
    </Svg>
);

export const Pin: React.FC<PanelIconProps> = (props) => (
    <Svg {...props}>
        <line x1="12" x2="12" y1="17" y2="22" />
        <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
    </Svg>
);

export const PinOff: React.FC<PanelIconProps> = (props) => (
    <Svg {...props}>
        <line x1="2" x2="22" y1="2" y2="22" />
        <line x1="12" x2="12" y1="17" y2="22" />
        <path d="M9 9v1.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8" />
    </Svg>
);

export const ChevronLeft: React.FC<PanelIconProps> = (props) => (
    <Svg {...props}>
        <path d="m15 18-6-6 6-6" />
    </Svg>
);

export const ChevronRight: React.FC<PanelIconProps> = (props) => (
    <Svg {...props}>
        <path d="m9 18 6-6-6-6" />
    </Svg>
);

export const ChevronUp: React.FC<PanelIconProps> = (props) => (
    <Svg {...props}>
        <path d="m18 15-6-6-6 6" />
    </Svg>
);

export const ChevronDown: React.FC<PanelIconProps> = (props) => (
    <Svg {...props}>
        <path d="m6 9 6 6 6-6" />
    </Svg>
);

export const Lock: React.FC<PanelIconProps> = (props) => (
    <Svg {...props}>
        <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
        <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </Svg>
);

export const LayoutPanelLeft: React.FC<PanelIconProps> = (props) => (
    <Svg {...props}>
        <rect width="18" height="18" x="3" y="3" rx="2" />
        <path d="M9 3v18" />
        <path d="M9 12h12" />
    </Svg>
);

export const PanelLeft: React.FC<PanelIconProps> = (props) => (
    <Svg {...props}>
        <rect width="18" height="18" x="3" y="3" rx="2" />
        <path d="M9 3v18" />
    </Svg>
);

export const PanelRight: React.FC<PanelIconProps> = (props) => (
    <Svg {...props}>
        <rect width="18" height="18" x="3" y="3" rx="2" />
        <path d="M15 3v18" />
    </Svg>
);

export const PanelTop: React.FC<PanelIconProps> = (props) => (
    <Svg {...props}>
        <rect width="18" height="18" x="3" y="3" rx="2" />
        <path d="M3 9h18" />
    </Svg>
);

export const PanelBottom: React.FC<PanelIconProps> = (props) => (
    <Svg {...props}>
        <rect width="18" height="18" x="3" y="3" rx="2" />
        <path d="M3 15h18" />
    </Svg>
);

export const AlignStartVertical: React.FC<PanelIconProps> = (props) => (
    <Svg {...props}>
        <rect width="9" height="6" x="4" y="16" rx="1" />
        <rect width="9" height="14" x="4" y="2" rx="1" />
        <line x1="22" x2="22" y1="2" y2="22" />
    </Svg>
);

export const AlignEndVertical: React.FC<PanelIconProps> = (props) => (
    <Svg {...props}>
        <rect width="9" height="6" x="4" y="2" rx="1" />
        <rect width="9" height="14" x="4" y="8" rx="1" />
        <line x1="22" x2="22" y1="2" y2="22" />
    </Svg>
);

export const AlignStartHorizontal: React.FC<PanelIconProps> = (props) => (
    <Svg {...props}>
        <rect width="6" height="9" x="16" y="4" rx="1" />
        <rect width="14" height="9" x="2" y="4" rx="1" />
        <line x1="2" x2="22" y1="22" y2="22" />
    </Svg>
);

export const AlignEndHorizontal: React.FC<PanelIconProps> = (props) => (
    <Svg {...props}>
        <rect width="6" height="9" x="2" y="4" rx="1" />
        <rect width="14" height="9" x="8" y="4" rx="1" />
        <line x1="2" x2="22" y1="2" y2="2" />
    </Svg>
);

export const AlignHorizontalJustifyCenter: React.FC<PanelIconProps> = (props) => (
    <Svg {...props}>
        <rect width="6" height="14" x="2" y="5" rx="1" />
        <rect width="6" height="10" x="16" y="7" rx="1" />
        <line x1="12" x2="12" y1="2" y2="22" />
    </Svg>
);

export const AlignVerticalJustifyCenter: React.FC<PanelIconProps> = (props) => (
    <Svg {...props}>
        <rect width="14" height="6" x="5" y="2" rx="1" />
        <rect width="10" height="6" x="7" y="16" rx="1" />
        <line x1="2" x2="22" y1="12" y2="12" />
    </Svg>
);
